import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Alert, Snackbar, useMediaQuery } from "@mui/material";

// Avisos breves ("Guardado", "No se pudo guardar"). Un solo aviso a la vez: el nuevo reemplaza al anterior.
// Uso: envolver la app en <AvisosProvider> (lo monta la fase W0) y, en cualquier pantalla,
//   const aviso = useAviso();  aviso.exito("Abono guardado");

const sinAviso = () => {};
const SIN_PROVIDER = { exito: sinAviso, error: sinAviso, info: sinAviso };

const AvisosContext = createContext(SIN_PROVIDER);

// Para avisar una sola vez (en desarrollo) de que falta el provider.
let avisoSinProviderEmitido = false;

// Los errores se quedan más tiempo en pantalla: hay que poder leerlos.
const DURACION = { success: 4000, info: 5000, error: 8000 };

// Clave de theme.palette.soft para cada tipo.
const FONDO = { success: "success", info: "primary", error: "error" };

export const AvisosProvider = ({ children }) => {
  const [aviso, setAviso] = useState({
    id: 0,
    abierto: false,
    tipo: "info",
    mensaje: "",
  });
  const reducirMovimiento = useMediaQuery("(prefers-reduced-motion: reduce)");

  const mostrar = useCallback((tipo, mensaje) => {
    setAviso((anterior) => ({
      id: anterior.id + 1,
      abierto: true,
      tipo,
      mensaje,
    }));
  }, []);

  const api = useMemo(
    () => ({
      exito: (mensaje) => mostrar("success", mensaje),
      error: (mensaje) => mostrar("error", mensaje),
      info: (mensaje) => mostrar("info", mensaje),
    }),
    [mostrar]
  );

  const cerrar = (_evento, razon) => {
    // Un clic fuera no debe borrar el aviso antes de que se lea.
    if (razon === "clickaway") return;
    setAviso((anterior) => ({ ...anterior, abierto: false }));
  };

  return (
    <AvisosContext.Provider value={api}>
      {children}
      <Snackbar
        key={aviso.id}
        open={aviso.abierto}
        autoHideDuration={DURACION[aviso.tipo]}
        onClose={cerrar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionProps={reducirMovimiento ? { timeout: 0 } : undefined}
      >
        <Alert
          severity={aviso.tipo}
          // Los errores interrumpen al lector de pantalla; el resto se anuncia con calma.
          role={aviso.tipo === "error" ? "alert" : "status"}
          onClose={cerrar}
          closeText="Cerrar"
          sx={(theme) => ({
            width: "100%",
            alignItems: "center",
            backgroundColor: theme.palette.soft?.[FONDO[aviso.tipo]],
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette[aviso.tipo].main}`,
            "& .MuiAlert-icon": { color: theme.palette[aviso.tipo].main },
          })}
        >
          {aviso.mensaje}
        </Alert>
      </Snackbar>
    </AvisosContext.Provider>
  );
};

/**
 * Devuelve { exito(msg), error(msg), info(msg) }.
 * Fuera de un AvisosProvider las funciones no hacen nada (no rompen la pantalla).
 */
export const useAviso = () => {
  const api = useContext(AvisosContext);
  if (
    api === SIN_PROVIDER &&
    !avisoSinProviderEmitido &&
    process.env.NODE_ENV !== "production"
  ) {
    avisoSinProviderEmitido = true;
    console.warn(
      "useAviso se usa fuera de un <AvisosProvider>: los avisos no se mostrarán. Monta el provider en la raíz de la app."
    );
  }
  return api;
};
